import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  CustomAutoComplete,
  CustomTextField,
  DecimalTextField,
  DialogActions,
  DialogBody,
  DialogContainer,
  DialogTitle,
  GeneralTableBox,
  HeadTableCell,
  InfoTooltip,
  NoDataSkeleton,
  renderTwoLineOption,
  RowTableCell,
} from '../../../common';
import {
  AUTOCOMPLETE_LISTBOX_PROPS,
  TABLE_INPUT_CELL_SX,
  TABLE_INPUT_SX,
} from '../../../common/styles';
import {
  SOURCE_TYPE_LABELS,
  SUPPLY_ORDER_FORM_TEXTS,
  SUPPLY_ORDER_HELP_TEXTS,
  SUPPLY_ORDER_ITEM_TABLE_TEXTS as ITEM_TEXTS,
  SUPPLY_ORDER_PRICE_TEXTS,
} from '../constants/supplyOrdersConstants';
import {
  buildGroupsFromExternalOrderItems,
  buildQuantityOnlyProviderGroups,
  formatSupplyOrderQuantity,
  getMissingExternalOrderItems,
  getQuantityLeftToOrder,
  getSupplyOrderApiErrorMessage,
} from '../utils/supplyOrdersUtils';
import { getArticleLabel } from '../../internal-orders/utils/internalOrdersUtils';
import { useInventoryArticlesByUuids } from '../../internal-orders/hooks/useInventoryArticlesByUuids';
import { guardDialogClose } from '../../utils/actionLockUtils';
import { toProviderOptions } from '../../utils/toProviderOptions';
import { toWarehouseOptions } from '../../utils/toWarehouseOptions';
import { MATERIAL_OPTION_TEXTS } from '../../constants/materialOptionTexts';

import ClientPaginatedTable from '../../../common/components/table/ClientPaginatedTable';
const textSx = { textAlign: 'left' };
const numberSx = { textAlign: 'right' };
const inputCellSx = TABLE_INPUT_CELL_SX;
const MIN_TABLE_WIDTH = { providerOnly: 640, full: 1280 };

const BASE_COLUMNS = [
  { key: 'select', label: '', sx: { width: 56 } },
  { key: 'article', label: ITEM_TEXTS.article, sx: textSx },
  { key: 'left', label: SUPPLY_ORDER_PRICE_TEXTS.leftToOrder, sx: { ...numberSx, width: 120 } },
  { key: 'quantity', label: ITEM_TEXTS.quantity, sx: { ...numberSx, width: 162 } },
];

const SOURCE_COLUMNS = [
  { key: 'commitment', label: ITEM_TEXTS.commitment, sx: { ...textSx, width: 192 } },
  { key: 'source', label: ITEM_TEXTS.source, sx: { ...textSx, width: 162 } },
  { key: 'origin', label: ITEM_TEXTS.providerOrWarehouse, sx: { ...textSx, width: 272 } },
];

const GenerateSupplyOrdersDialog = ({
  open,
  onClose,
  externalOrder,
  warehouses = [],
  providers = [],
  onGenerate,
  isProcessing = false,
  providerOnly = false,
}) => {
  const [destinationWarehouse, setDestinationWarehouse] = useState('');
  const [rows, setRows] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [resultOrders, setResultOrders] = useState([]);

  const missingItems = useMemo(
    () => getMissingExternalOrderItems(externalOrder),
    [externalOrder]
  );

  const articleUuids = useMemo(
    () => missingItems.map((item) => item.uuid_inventory_article),
    [missingItems]
  );

  const { articles: inventoryArticles } = useInventoryArticlesByUuids(
    articleUuids,
    open && missingItems.length > 0
  );

  const articlesByUuid = useMemo(
    () =>
      inventoryArticles.reduce((acc, article) => {
        if (article?.uuid) acc[article.uuid] = article;
        return acc;
      }, {}),
    [inventoryArticles]
  );

  useEffect(() => {
    if (open && externalOrder) {
      setDestinationWarehouse(externalOrder.uuid_destination_warehouse || '');
      setResultOrders([]);
      setErrorMessage('');
      setRows(
        missingItems.map((item) => ({
          selected: true,
          group_name: item.group_name || 'Sin grupo',
          uuid_external_order_group: item.uuid_external_order_group,
          uuid_external_order_item: item.uuid,
          uuid_inventory_article: item.uuid_inventory_article,
          missing_quantity: getQuantityLeftToOrder(item),
          source_type: 'provider',
          uuid_provider_legal_entity: '',
          uuid_source_warehouse: '',
          provider_delivery_commitment_at: '',
          quantity: getQuantityLeftToOrder(item),
        }))
      );
    }
  }, [open, externalOrder, missingItems]);

  const columns = providerOnly ? BASE_COLUMNS : [...BASE_COLUMNS, ...SOURCE_COLUMNS];
  const warehouseOptions = toWarehouseOptions(warehouses);
  const providerOptions = toProviderOptions(providers);
  const groupOptions = [...new Set(rows.map((row) => row.group_name))].map((groupName) => ({
    value: groupName,
    label: groupName,
  }));

  const getArticleDisplay = (row) => {
    const article = articlesByUuid[row.uuid_inventory_article];
    return {
      name: article?.name || getArticleLabel(articlesByUuid, row.uuid_inventory_article),
      detail: [article?.external_code || article?.code, row.group_name]
        .filter(Boolean)
        .join(' · '),
    };
  };

  const sourceTypeOptions = Object.entries(SOURCE_TYPE_LABELS)
    .filter(([value]) => !providerOnly || value === 'provider')
    .map(([value, label]) => ({
      value,
      label,
    }));

  const handleRowChange = (index, field, value) => {
    setRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index
          ? {
              ...row,
              [field]: value,
              ...(field === 'source_type'
                ? {
                    uuid_provider_legal_entity: '',
                    uuid_source_warehouse: '',
                    provider_delivery_commitment_at: '',
                  }
                : {}),
            }
          : row
      )
    );
  };

  const handleGenerate = async () => {
    const groups = providerOnly
      ? buildQuantityOnlyProviderGroups(rows)
      : buildGroupsFromExternalOrderItems(rows);

    if (groups.length === 0) {
      setErrorMessage('Seleccioná al menos un artículo con cantidad válida.');
      return;
    }

    if (!providerOnly) {
      for (const group of groups) {
        if (group.source_type === 'provider' && !group.uuid_provider_legal_entity) {
          setErrorMessage('Seleccioná un proveedor para cada grupo de artículos.');
          return;
        }
        if (group.source_type === 'warehouse' && !group.uuid_source_warehouse) {
          setErrorMessage('Seleccioná un depósito origen para cada grupo de artículos.');
          return;
        }
      }
    }

    const payload = {
      issue_now: false,
      groups,
    };

    if (destinationWarehouse) {
      payload.uuid_destination_warehouse = destinationWarehouse;
    }

    try {
      setErrorMessage('');
      const createdOrders = await onGenerate({
        externalOrderId: externalOrder.uuid,
        payload,
      });
      setResultOrders(Array.isArray(createdOrders) ? createdOrders : [createdOrders]);
    } catch (error) {
      setErrorMessage(
        getSupplyOrderApiErrorMessage(error, 'No se pudieron generar las órdenes de abastecimiento')
      );
    }
  };

  if (!open) return null;

  return (
    <DialogContainer
      open={open}
      onClose={guardDialogClose(isProcessing, onClose)}
      size="large"
      minHeight="60vh"
    >
      <DialogTitle>
        {providerOnly ? 'Generar borrador a proveedor' : 'Generar órdenes de abastecimiento'}
      </DialogTitle>
      <DialogBody>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

          {resultOrders.length > 0 ? (
            <Alert severity="success">
              Se generaron {resultOrders.length} orden(es):
              <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                {resultOrders.map((order) => (
                  <li key={order.uuid}>
                    {order.order_code || 'Borrador'} — {order.status}
                  </li>
                ))}
              </Box>
            </Alert>
          ) : (
            <>
              <CustomAutoComplete
                label={SUPPLY_ORDER_FORM_TEXTS.destinationWarehouse}
                name="uuid_destination_warehouse"
                value={destinationWarehouse}
                options={warehouseOptions}
                renderOption={renderTwoLineOption}
                inputPlaceholder={SUPPLY_ORDER_FORM_TEXTS.searchWarehouse}
                ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
                onChange={(event) => setDestinationWarehouse(event.target.value)}
              />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Artículos con faltante
                </Typography>
                {providerOnly && (
                  <InfoTooltip lines={[SUPPLY_ORDER_HELP_TEXTS.draftFromTechnicalOrder]} />
                )}
              </Box>

              <CustomAutoComplete
                label={SUPPLY_ORDER_PRICE_TEXTS.selectGroup}
                options={groupOptions}
                inputPlaceholder={MATERIAL_OPTION_TEXTS.searchGroup}
                ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
                onChange={(event) =>
                  setRows((prev) =>
                    prev.map((row) => ({ ...row, selected: row.group_name === event.target.value }))
                  )
                }
              />
              <ClientPaginatedTable rows={rows} resetKey={externalOrder?.uuid}>
                {(pageRows, pageStart) => (
                  <GeneralTableBox
                    hasMaxHeight={false}
                    fixedLayout
                    minTableWidth={providerOnly ? MIN_TABLE_WIDTH.providerOnly : MIN_TABLE_WIDTH.full}
                  >
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          {columns.map((column) => (
                            <HeadTableCell key={column.key} sx={column.sx}>
                              {column.label}
                            </HeadTableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.length === 0 ? (
                          <NoDataSkeleton
                            colSpan={columns.length}
                            message={ITEM_TEXTS.emptyMissing}
                            showSubtitle={false}
                          />
                        ) : (
                          pageRows.map((row, index) => {
                            const articleDisplay = getArticleDisplay(row);
                            return (
                              <TableRow key={row.uuid_external_order_item} hover>
                                <RowTableCell sx={inputCellSx}>
                                  <Checkbox
                                    size="small"
                                    checked={row.selected}
                                    inputProps={{ 'aria-label': ITEM_TEXTS.select }}
                                    onChange={(event) =>
                                      handleRowChange(
                                        pageStart + index,
                                        'selected',
                                        event.target.checked
                                      )
                                    }
                                  />
                                </RowTableCell>
                                <RowTableCell sx={textSx} title={articleDisplay.name}>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 700, lineHeight: 1.25 }}
                                    noWrap
                                  >
                                    {articleDisplay.name}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: 'text.secondary' }}
                                    noWrap
                                  >
                                    {articleDisplay.detail}
                                  </Typography>
                                </RowTableCell>
                                <RowTableCell sx={numberSx}>
                                  {formatSupplyOrderQuantity(row.missing_quantity)}
                                </RowTableCell>
                                <RowTableCell sx={{ ...numberSx, ...inputCellSx }}>
                                  <DecimalTextField
                                    value={row.quantity}
                                    onChange={(event) =>
                                      handleRowChange(
                                        pageStart + index,
                                        'quantity',
                                        event.target.value
                                      )
                                    }
                                    inputProps={{ style: { textAlign: 'right' } }}
                                    size="small"
                                    sx={TABLE_INPUT_SX}
                                  />
                                </RowTableCell>
                                {!providerOnly && (
                                  <>
                                    <RowTableCell sx={{ ...textSx, ...inputCellSx }}>
                                      {row.source_type === 'provider' ? (
                                        <CustomTextField
                                          type="date"
                                          value={row.provider_delivery_commitment_at}
                                          onChange={(event) =>
                                            handleRowChange(
                                              pageStart + index,
                                              'provider_delivery_commitment_at',
                                              event.target.value
                                            )
                                          }
                                          size="small"
                                          sx={TABLE_INPUT_SX}
                                        />
                                      ) : (
                                        '-'
                                      )}
                                    </RowTableCell>
                                    <RowTableCell sx={{ ...textSx, ...inputCellSx }}>
                                      <CustomAutoComplete
                                        name="source_type"
                                        value={row.source_type}
                                        options={sourceTypeOptions}
                                        disableClearable
                                        onChange={(event) =>
                                          handleRowChange(
                                            pageStart + index,
                                            'source_type',
                                            event.target.value
                                          )
                                        }
                                        size="small"
                                        sx={TABLE_INPUT_SX}
                                      />
                                    </RowTableCell>
                                    <RowTableCell sx={{ ...textSx, ...inputCellSx }}>
                                      {row.source_type === 'provider' ? (
                                        <CustomAutoComplete
                                          name="uuid_provider_legal_entity"
                                          value={row.uuid_provider_legal_entity}
                                          options={providerOptions}
                                          renderOption={renderTwoLineOption}
                                          inputPlaceholder={ITEM_TEXTS.searchProvider}
                                          ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
                                          onChange={(event) =>
                                            handleRowChange(
                                              pageStart + index,
                                              'uuid_provider_legal_entity',
                                              event.target.value
                                            )
                                          }
                                          size="small"
                                          sx={TABLE_INPUT_SX}
                                        />
                                      ) : (
                                        <CustomAutoComplete
                                          name="uuid_source_warehouse"
                                          value={row.uuid_source_warehouse}
                                          options={warehouseOptions}
                                          renderOption={renderTwoLineOption}
                                          inputPlaceholder={ITEM_TEXTS.searchWarehouse}
                                          ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
                                          onChange={(event) =>
                                            handleRowChange(
                                              pageStart + index,
                                              'uuid_source_warehouse',
                                              event.target.value
                                            )
                                          }
                                          size="small"
                                          sx={TABLE_INPUT_SX}
                                        />
                                      )}
                                    </RowTableCell>
                                  </>
                                )}
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </GeneralTableBox>
                )}
              </ClientPaginatedTable>
            </>
          )}
        </Box>
      </DialogBody>
      <DialogActions
        handleClose={onClose}
        handleSave={resultOrders.length > 0 ? onClose : handleGenerate}
        saveLabel={
          resultOrders.length > 0 ? 'Cerrar' : providerOnly ? 'Generar borrador' : 'Generar órdenes'
        }
        saveButtonProps={{ isLoading: isProcessing }}
      />
    </DialogContainer>
  );
};

export default GenerateSupplyOrdersDialog;
